import { useCallback, useEffect, useRef, useState } from 'react';

import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';
import type { ClarifyingQuestion, QuestionAnswer } from '@revelio/core';
import { getDashboardWidgetPromptKeywords } from '@revelio/core/dashboard-capabilities';
import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import {
  getRoleTaxonomyIdForAiSearch,
  getSkillTaxonomyIdFromActiveFilterMap,
} from '../../filters/ai-search/getAiSearchTaxonomyFromFilterMap';
import { useApplyAiSearchFilters } from '../../filters/ai-search/useApplyAiSearchFilters';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import type { PrimaryFilterId } from '../../filters/types/filters';
import type {
  TabContent,
  TabContentAction,
} from '../PageConfigProvider/page-reducer';
import type { SecondaryDimensionValues } from '../PageConfigProvider/types/dimension';
import { convertTabContentToLLM } from './convert-tab-content-to-llm';
import {
  applyLlmTabContentToTabDispatch,
  PRIMARY_FILTER_BY_AI_ENTITY,
} from './llm-tab-content-dispatch';

/** Entity types supported by AI page generation (excludes 'report') */
export type AIEntityType = 'company' | 'geography' | 'role';

interface DashboardIntent {
  summary: string;
  title?: string;
  widgets: Array<{
    analysisType: string;
    metric: string;
    compareMetric?: string;
    breakdownDimension?: string;
    breakdownGoal?: string;
    preferredView?: string;
    layoutGroup?: string;
    widthPreference?: string;
    priority: string;
    title?: string;
    rationale?: string;
    options?: {
      topN?: number;
      sort?: 'asc' | 'desc';
    };
  }>;
  secondaryFilters: string[];
}

type SSEEvent =
  | { type: 'thinking'; content: string }
  | { type: 'status'; content: string }
  | { type: 'intent'; content: DashboardIntent }
  | { type: 'config'; content: LLMTabContent }
  | { type: 'filters'; content: TalentDiscoveryAiFilterSearchResponseV2 }
  | { type: 'error'; content: string }
  | {
      type: 'questions';
      content: { questions: ClarifyingQuestion[]; triggerReason: string };
    };

// Simplified TabContent from LLM
export interface LLMTabContent {
  items: LLMPositionedItem[];
  grid: { rows: number; columns: number };
  secondaryFilters: { id: string }[];
}

interface LLMPositionedItem {
  title: string | null;
  position: { x: number; y: number; width: number; height: number };
  plot?: {
    chartType: string;
    defaultMetricOptions: string[];
    secondaryDimension?: string;
    plotFilters?: Array<{ type: string; value: number }>;
    dimensionSource?: {
      type: 'PRIMARY_DIMENSION' | 'TOP_SEGMENTS';
      segment?: SecondaryDimensionValues;
    };
    color?: string;
    showDataLabels?: boolean;
    showZebraStripe?: boolean;
  };
}

interface UseAIAssistantOptions {
  entityType: AIEntityType;
  getCurrentTabContent: () => TabContent;
  dispatch: (action: TabContentAction) => void;
}

interface ConversationListItem {
  id: string;
  title: string;
  updatedAt: string;
}

interface UseAIAssistantResult {
  thinking: string[];
  status: string | null;
  intent: DashboardIntent | null;
  isGenerating: boolean;
  isFixing: boolean;
  isLoadingConversation: boolean;
  error: string | null;
  promptHistory: string[];
  configSnapshots: LLMTabContent[];
  responseSummaries: string[];
  conversationTitle: string | null;
  questions: ClarifyingQuestion[] | null;
  conversations: ConversationListItem[];
  conversationId: string | null;
  continueWithAnswers: (answers: QuestionAnswer[]) => Promise<void>;
  skipQuestions: () => Promise<void>;
  generate: (prompt: string, model?: string) => Promise<void>;
  update: (newPrompt: string, model?: string) => Promise<void>;
  fixCharts: (
    originalPrompt: string,
    chartErrors: Array<{
      chartId: string;
      chartTitle?: string;
      chartType: string;
      metric: string;
      secondaryDimension?: string;
      reason: string;
    }>,
    model?: string
  ) => Promise<void>;
  restoreConfig: (index: number) => void;
  reset: () => void;
  saveConversation: () => Promise<void>;
  loadConversation: (id: string) => Promise<void>;
  fetchConversations: () => Promise<void>;
}

const PRIMARY_FILTER_MAP: Record<AIEntityType, PrimaryFilterId> =
  PRIMARY_FILTER_BY_AI_ENTITY;

function lowerCaseFirstCharacter(input: string): string {
  if (input.length === 0) {
    return input;
  }

  return input.charAt(0).toLowerCase() + input.slice(1);
}

function buildDashboardContextExplanation(
  prompt: string,
  intent: DashboardIntent
): string | null {
  const lowerPrompt = prompt.toLowerCase();
  if (
    !(
      /\b(why did you add|why is there|why include|what's the reason for|what is the reason for)\b/.test(
        lowerPrompt
      ) || /\bwhy was\b.*\badded\b/.test(lowerPrompt)
    )
  ) {
    return null;
  }

  const matchedWidget = intent.widgets.find((widget) =>
    getDashboardWidgetPromptKeywords(widget).some((keyword) =>
      lowerPrompt.includes(keyword)
    )
  );

  if (matchedWidget && !matchedWidget.rationale) {
    return null;
  }

  const candidate =
    matchedWidget ?? intent.widgets.find((widget) => widget.rationale);

  if (!candidate?.rationale) {
    return null;
  }

  const title = candidate.title ?? candidate.metric.replace(/_/g, ' ');
  return `I added ${title} because ${lowerCaseFirstCharacter(candidate.rationale)}`;
}

export function useAIAssistant({
  entityType,
  getCurrentTabContent,
  dispatch,
}: UseAIAssistantOptions): UseAIAssistantResult {
  const [thinking, setThinking] = useState<string[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [intent, setIntent] = useState<DashboardIntent | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promptHistory, setPromptHistory] = useState<string[]>([]);
  const [configSnapshots, setConfigSnapshots] = useState<LLMTabContent[]>([]);
  const [responseSummaries, setResponseSummaries] = useState<string[]>([]);
  const [conversationTitle, setConversationTitle] = useState<string | null>(
    null
  );
  // Sync ref with state for use in nested callbacks
  useEffect(() => {
    conversationTitleRef.current = conversationTitle;
  }, [conversationTitle]);
  const [questions, setQuestions] = useState<ClarifyingQuestion[] | null>(null);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const [pendingModel, setPendingModel] = useState<string | undefined>(
    undefined
  );
  const [pendingPromptHistory, setPendingPromptHistory] = useState<string[]>(
    []
  );
  const [conversations, setConversations] = useState<ConversationListItem[]>(
    []
  );
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isLoadingConversationRef = useRef<boolean>(false);
  const latestIntentRef = useRef<DashboardIntent | null>(null);
  const conversationTitleRef = useRef<string | null>(null);

  const { activeFilterMap } = useFilterContext();
  const { applyAiSearchFilters } = useApplyAiSearchFilters();

  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setThinking([]);
    setStatus(null);
    setIntent(null);
    setIsGenerating(false);
    setIsFixing(false);
    setError(null);
    setPromptHistory([]);
    setConfigSnapshots([]);
    setResponseSummaries([]);
    setConversationTitle(null);
    conversationTitleRef.current = null;
    latestIntentRef.current = null;
    setQuestions(null);
    setPendingPrompt(null);
    setPendingModel(undefined);
    setPendingPromptHistory([]);
    setConversationId(null);
  }, []);

  const generate = useCallback(
    async (prompt: string, model?: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsGenerating(true);
      setThinking([]);
      setStatus(null);
      setIntent(null);
      setError(null);
      setQuestions(null);
      setPendingPrompt(null);
      setPendingModel(undefined);
      setPendingPromptHistory([]);
      setPromptHistory((prev) => [...prev, prompt]);

      const csrfToken = getAuthDashMetaCsrfToken();
      const rolesTax = getRoleTaxonomyIdForAiSearch(activeFilterMap);
      const skillsTax = getSkillTaxonomyIdFromActiveFilterMap(activeFilterMap);
      const taxonomyPayload = {
        ...(rolesTax && { rolesTax }),
        ...(skillsTax !== 0 && { skillsTax }),
      };

      try {
        const response = await fetch(
          `${DASH_META_ROOT}/api/build-a-dash/ai-gen/page-stream`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'x-surf-token-dash-meta': csrfToken ?? '',
            },
            body: JSON.stringify({
              prompt,
              entityType,
              ...(model && { model }),
              ...taxonomyPayload,
            }),
            signal: abortController.signal,
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processLine = (line: string) => {
          if (!line.startsWith('data: ')) {
            return;
          }

          const jsonStr = line.slice(6);
          if (!jsonStr.trim()) {
            return;
          }

          try {
            const event: SSEEvent = JSON.parse(jsonStr);

            switch (event.type) {
              case 'thinking':
                setThinking((prev) => {
                  const updated = [...prev, event.content];
                  return updated;
                });
                break;
              case 'status':
                setStatus(event.content);
                break;
              case 'intent':
                setIntent(event.content);
                latestIntentRef.current = event.content;
                // Set conversation title from first intent (only if not already set)
                if (!conversationTitleRef.current && event.content.title) {
                  setConversationTitle(event.content.title);
                }
                break;
              case 'config': {
                const primaryFilterId = PRIMARY_FILTER_MAP[entityType];
                applyLlmTabContentToTabDispatch(
                  dispatch,
                  event.content,
                  primaryFilterId
                );

                // Store snapshot for restore functionality
                setConfigSnapshots((prev) => [...prev, event.content]);
                const currentIntent = latestIntentRef.current;
                setResponseSummaries((prev) => [
                  ...prev,
                  currentIntent?.summary ?? '',
                ]);
                break;
              }
              case 'filters': {
                applyAiSearchFilters(event.content).catch((filterError) => {
                  console.error(
                    'Failed to apply AI search filters',
                    filterError
                  );
                });
                break;
              }
              case 'questions':
                setQuestions(event.content.questions);
                setPendingPrompt(prompt);
                setPendingModel(model);
                setPendingPromptHistory([]);
                setIsGenerating(false);
                abortController.abort();
                break;
              case 'error':
                setError(event.content);
                break;
            }
            // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
          } catch (parseError) {
            // Ignore parse errors for incomplete chunks
          }
        };

        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();

            if (done) break;

            const decoded = decoder.decode(value, { stream: true });
            buffer += decoded;

            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';

            lines.forEach(processLine);
          }

          if (buffer.trim()) {
            processLine(buffer);
          }
        } finally {
          reader.cancel().catch(() => {
            // Ignore cancel errors (stream may already be closed)
          });
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        // Roll back prompt so we keep 1:1 with responseSummaries
        setPromptHistory((prev) => prev.slice(0, -1));
      } finally {
        setIsGenerating(false);
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [entityType, dispatch, applyAiSearchFilters, activeFilterMap]
  );

  const update = useCallback(
    async (newPrompt: string, model?: string) => {
      const explanation = latestIntentRef.current
        ? buildDashboardContextExplanation(newPrompt, latestIntentRef.current)
        : null;

      if (explanation) {
        setThinking([]);
        setStatus(null);
        setError(null);
        setQuestions(null);
        setPendingPrompt(null);
        setPendingModel(undefined);
        setPendingPromptHistory([]);
        setPromptHistory((prev) => [...prev, newPrompt]);
        setResponseSummaries((prev) => [...prev, explanation]);
        return;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsGenerating(true);
      setThinking([]);
      setStatus(null);
      setError(null);
      setQuestions(null);
      setPendingPrompt(null);
      setPendingModel(undefined);
      setPendingPromptHistory([]);
      setPromptHistory((prev) => [...prev, newPrompt]);

      // Get current config and convert to LLM format
      const currentTabContent = getCurrentTabContent();
      const currentConfig = convertTabContentToLLM(currentTabContent);

      const csrfToken = getAuthDashMetaCsrfToken();
      const rolesTax = getRoleTaxonomyIdForAiSearch(activeFilterMap);
      const skillsTax = getSkillTaxonomyIdFromActiveFilterMap(activeFilterMap);
      const taxonomyPayload = {
        ...(rolesTax && { rolesTax }),
        ...(skillsTax !== 0 && { skillsTax }),
      };

      try {
        const response = await fetch(
          `${DASH_META_ROOT}/api/build-a-dash/ai-gen/page-update-from-config-stream`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'x-surf-token-dash-meta': csrfToken ?? '',
            },
            body: JSON.stringify({
              newPrompt,
              currentConfig,
              previousPrompts: promptHistory.slice(-3),
              entityType,
              ...(model && { model }),
              ...taxonomyPayload,
            }),
            signal: abortController.signal,
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processLine = (line: string) => {
          if (!line.startsWith('data: ')) {
            return;
          }

          const jsonStr = line.slice(6);
          if (!jsonStr.trim()) {
            return;
          }

          try {
            const event: SSEEvent = JSON.parse(jsonStr);

            switch (event.type) {
              case 'thinking':
                setThinking((prev) => {
                  const updated = [...prev, event.content];
                  return updated;
                });
                break;
              case 'status':
                setStatus(event.content);
                break;
              case 'intent':
                setIntent(event.content);
                latestIntentRef.current = event.content;
                // Set conversation title from first intent (only if not already set)
                if (!conversationTitleRef.current && event.content.title) {
                  setConversationTitle(event.content.title);
                }
                break;
              case 'config': {
                const primaryFilterId = PRIMARY_FILTER_MAP[entityType];
                applyLlmTabContentToTabDispatch(
                  dispatch,
                  event.content,
                  primaryFilterId
                );

                // Store snapshot for restore functionality
                setConfigSnapshots((prev) => [...prev, event.content]);
                const currentIntent = latestIntentRef.current;
                setResponseSummaries((prev) => [
                  ...prev,
                  currentIntent?.summary ?? '',
                ]);
                break;
              }
              case 'filters': {
                applyAiSearchFilters(event.content).catch((filterError) => {
                  console.error(
                    'Failed to apply AI search filters',
                    filterError
                  );
                });
                break;
              }
              case 'questions':
                setQuestions(event.content.questions);
                setPendingPrompt(newPrompt);
                setPendingModel(model);
                setPendingPromptHistory(promptHistory);
                setIsGenerating(false);
                abortController.abort();
                break;
              case 'error':
                setError(event.content);
                break;
            }
            // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
          } catch (parseError) {
            // Ignore parse errors for incomplete chunks
          }
        };

        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();

            if (done) break;

            const decoded = decoder.decode(value, { stream: true });
            buffer += decoded;

            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';

            lines.forEach(processLine);
          }

          if (buffer.trim()) {
            processLine(buffer);
          }
        } finally {
          reader.cancel().catch(() => {
            // Ignore cancel errors (stream may already be closed)
          });
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        setPromptHistory((prev) => prev.slice(0, -1));
      } finally {
        setIsGenerating(false);
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [
      entityType,
      getCurrentTabContent,
      promptHistory,
      dispatch,
      applyAiSearchFilters,
      activeFilterMap,
    ]
  );

  const continueWithAnswers = useCallback(
    async (answers: QuestionAnswer[]) => {
      if (!pendingPrompt) {
        return;
      }

      setQuestions(null);
      const promptToUse = pendingPrompt;
      const modelToUse = pendingModel;
      const historyToUse = pendingPromptHistory;

      setPendingPrompt(null);
      setPendingModel(undefined);
      setPendingPromptHistory([]);

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsGenerating(true);
      setThinking([]);
      setStatus(null);
      setError(null);

      const csrfToken = getAuthDashMetaCsrfToken();

      // Determine if this is an update or generate
      const currentTabContent = getCurrentTabContent();
      const hasExistingItems = currentTabContent.items.length > 0;
      const currentConfig = convertTabContentToLLM(currentTabContent);

      const endpoint = hasExistingItems
        ? `${DASH_META_ROOT}/api/build-a-dash/ai-gen/page-update-from-config-stream`
        : `${DASH_META_ROOT}/api/build-a-dash/ai-gen/page-stream`;

      const taxonomyPayload = (() => {
        const rolesTax = getRoleTaxonomyIdForAiSearch(activeFilterMap);
        const skillsTax =
          getSkillTaxonomyIdFromActiveFilterMap(activeFilterMap);
        return {
          ...(rolesTax && { rolesTax }),
          ...(skillsTax !== 0 && { skillsTax }),
        };
      })();

      const requestBody = hasExistingItems
        ? {
            newPrompt: promptToUse,
            currentConfig,
            previousPrompts: historyToUse.slice(-3),
            entityType,
            ...(modelToUse && { model: modelToUse }),
            answers,
            ...taxonomyPayload,
          }
        : {
            prompt: promptToUse,
            entityType,
            ...(modelToUse && { model: modelToUse }),
            answers,
            ...taxonomyPayload,
          };

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'x-surf-token-dash-meta': csrfToken ?? '',
          },
          body: JSON.stringify(requestBody),
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processLine = (line: string) => {
          if (!line.startsWith('data: ')) return;
          const jsonStr = line.slice(6);
          if (!jsonStr.trim()) return;

          try {
            const event: SSEEvent = JSON.parse(jsonStr);
            switch (event.type) {
              case 'thinking':
                setThinking((prev) => [...prev, event.content]);
                break;
              case 'status':
                setStatus(event.content);
                break;
              case 'intent':
                setIntent(event.content);
                latestIntentRef.current = event.content;
                // Set conversation title from first intent (only if not already set)
                if (!conversationTitleRef.current && event.content.title) {
                  setConversationTitle(event.content.title);
                }
                break;
              case 'config': {
                const primaryFilterId = PRIMARY_FILTER_MAP[entityType];
                applyLlmTabContentToTabDispatch(
                  dispatch,
                  event.content,
                  primaryFilterId
                );

                // Store snapshot for restore functionality
                setConfigSnapshots((prev) => [...prev, event.content]);
                setPromptHistory((prev) => [...prev, promptToUse]);
                // Capture summary from latest intent when config arrives
                const currentIntent = latestIntentRef.current;
                if (currentIntent?.summary) {
                  setResponseSummaries((prev) => [
                    ...prev,
                    currentIntent.summary,
                  ]);
                }
                break;
              }
              case 'filters': {
                applyAiSearchFilters(event.content).catch((filterError) => {
                  console.error(
                    'Failed to apply AI search filters',
                    filterError
                  );
                });
                break;
              }
              case 'error':
                setError(event.content);
                break;
            }
          } catch {
            // Ignore parse errors
          }
        };

        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              break;
            }
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';
            lines.forEach(processLine);
          }

          if (buffer.trim()) {
            processLine(buffer);
          }
        } finally {
          reader.cancel().catch(() => {
            // Ignore cancel errors (stream may already be closed)
          });
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
      } finally {
        setIsGenerating(false);
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [
      pendingPrompt,
      pendingModel,
      pendingPromptHistory,
      getCurrentTabContent,
      entityType,
      dispatch,
      applyAiSearchFilters,
      activeFilterMap,
    ]
  );

  const skipQuestions = useCallback(async () => {
    if (!pendingPrompt || !questions) return;

    const skippedAnswers: QuestionAnswer[] = questions.map((q) => ({
      questionId: q.id,
      selectedOptionIds: ['best_judgement'],
    }));

    await continueWithAnswers(skippedAnswers);
  }, [pendingPrompt, questions, continueWithAnswers]);

  const fixCharts = useCallback(
    async (
      originalPrompt: string,
      chartErrors: Array<{
        chartId: string;
        chartTitle?: string;
        chartType: string;
        metric: string;
        secondaryDimension?: string;
        reason: string;
      }>,
      model?: string
    ) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsFixing(true);
      setThinking((prev) => [
        ...prev,
        '\n\n--- Fixing chart issues ---\n',
        `Found ${chartErrors.length} chart(s) with issues:\n`,
        ...chartErrors.map(
          (err) =>
            `- ${err.chartTitle || err.chartId}: ${err.chartType} + ${err.metric}${err.secondaryDimension ? ` (${err.secondaryDimension})` : ''} - ${err.reason}\n`
        ),
      ]);
      setStatus('Fixing charts...');
      setError(null);

      const csrfToken = getAuthDashMetaCsrfToken();

      try {
        const response = await fetch(
          `${DASH_META_ROOT}/api/build-a-dash/ai-gen/fix-charts`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'x-surf-token-dash-meta': csrfToken ?? '',
            },
            body: JSON.stringify({
              originalPrompt,
              entityType,
              chartErrors: chartErrors.map((err) => ({
                chartId: err.chartId,
                chartTitle: err.chartTitle,
                chartType: err.chartType,
                metric: err.metric,
                secondaryDimension: err.secondaryDimension,
                reason: err.reason,
              })),
              currentConfig: convertTabContentToLLM(getCurrentTabContent()),
              ...(model && { model }),
            }),
            signal: abortController.signal,
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processLine = (line: string) => {
          if (!line.startsWith('data: ')) return;

          const jsonStr = line.slice(6);
          if (!jsonStr.trim()) return;

          try {
            const event: SSEEvent = JSON.parse(jsonStr);

            switch (event.type) {
              case 'thinking':
                setThinking((prev) => [...prev, event.content]);
                break;
              case 'status':
                setStatus(event.content);
                break;
              case 'config': {
                const primaryFilterId = PRIMARY_FILTER_MAP[entityType];
                applyLlmTabContentToTabDispatch(
                  dispatch,
                  event.content,
                  primaryFilterId
                );
                break;
              }
              case 'error':
                setError(event.content);
                break;
            }
          } catch {
            // Ignore parse errors
          }
        };

        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();

            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';

            lines.forEach(processLine);
          }

          if (buffer.trim()) {
            processLine(buffer);
          }
        } finally {
          reader.cancel().catch(() => {
            // Ignore cancel errors (stream may already be closed)
          });
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
      } finally {
        setIsFixing(false);
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [entityType, getCurrentTabContent, dispatch]
  );

  const restoreConfig = useCallback(
    (index: number) => {
      const snapshot = configSnapshots[index];
      if (!snapshot) {
        return;
      }

      const primaryFilterId = PRIMARY_FILTER_MAP[entityType];
      applyLlmTabContentToTabDispatch(dispatch, snapshot, primaryFilterId);
    },
    [configSnapshots, entityType, dispatch]
  );

  const fetchConversations = useCallback(async () => {
    const csrfToken = getAuthDashMetaCsrfToken();

    try {
      const response = await fetch(
        `${DASH_META_ROOT}/api/build-a-dash/ai-gen/conversations?entityType=${entityType}&pageType=dashboard`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'x-surf-token-dash-meta': csrfToken ?? '',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch conversations: ${response.status}`);
      }

      const data = await response.json();
      setConversations(data);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
  }, [entityType]);

  const saveConversation = useCallback(async () => {
    if (promptHistory.length === 0 || configSnapshots.length === 0 || !intent) {
      return;
    }

    const csrfToken = getAuthDashMetaCsrfToken();
    // Title is set once from first intent, never updated
    const title =
      conversationTitle ||
      intent.title ||
      intent.summary?.substring(0, 50) ||
      promptHistory[0]?.substring(0, 50) ||
      'Untitled Conversation';

    try {
      if (conversationId) {
        // Update existing conversation - don't update title
        const res = await fetch(
          `${DASH_META_ROOT}/api/build-a-dash/ai-gen/conversations/${conversationId}`,
          {
            method: 'PUT',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'x-surf-token-dash-meta': csrfToken ?? '',
            },
            body: JSON.stringify({
              promptHistory,
              configSnapshots,
              latestIntent: intent,
              responseSummaries,
            }),
          }
        );
        if (res.ok) {
          await fetchConversations();
        }
      } else {
        // Create new conversation
        const response = await fetch(
          `${DASH_META_ROOT}/api/build-a-dash/ai-gen/conversations`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'x-surf-token-dash-meta': csrfToken ?? '',
            },
            body: JSON.stringify({
              entityType,
              pageType: 'dashboard',
              title,
              promptHistory,
              configSnapshots,
              latestIntent: intent,
              responseSummaries,
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          setConversationId(data.id);
          await fetchConversations();
        }
      }
    } catch (err) {
      console.error('Failed to save conversation:', err);
    }
  }, [
    conversationId,
    entityType,
    intent,
    promptHistory,
    configSnapshots,
    responseSummaries,
    conversationTitle,
    fetchConversations,
  ]);

  const loadConversation = useCallback(async (id: string) => {
    const csrfToken = getAuthDashMetaCsrfToken();

    // Set flag to prevent auto-save from triggering during load
    isLoadingConversationRef.current = true;
    setIsLoadingConversation(true);

    try {
      const response = await fetch(
        `${DASH_META_ROOT}/api/build-a-dash/ai-gen/conversations/${id}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'x-surf-token-dash-meta': csrfToken ?? '',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load conversation: ${response.status}`);
      }

      const conversation = await response.json();

      // Hydrate state from loaded conversation (without auto-restoring dashboard)
      setConversationId(conversation.id);
      setPromptHistory(conversation.promptHistory || []);
      setConfigSnapshots(conversation.configSnapshots || []);
      setResponseSummaries(conversation.responseSummaries || []);
      setConversationTitle(conversation.title || null);
      setIntent(conversation.latestIntent || null);
      setQuestions(null);

      // Note: Dashboard restoration is handled manually by user clicking prompts
      // in the conversation history section, not automatically on load
    } catch (err) {
      console.error('Failed to load conversation:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load conversation'
      );
    } finally {
      setIsLoadingConversation(false);
      // Reset flag after a short delay to allow state updates to complete
      // This ensures auto-save won't trigger from the state changes during load
      setTimeout(() => {
        isLoadingConversationRef.current = false;
      }, 100);
    }
  }, []);

  // Auto-save conversation when config is successfully generated
  useEffect(() => {
    if (
      configSnapshots.length > 0 &&
      promptHistory.length > 0 &&
      intent &&
      !isGenerating &&
      !isLoadingConversationRef.current
    ) {
      // Debounce saves to avoid too many API calls
      const timeoutId = setTimeout(() => {
        saveConversation();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [
    configSnapshots.length,
    promptHistory.length,
    intent,
    isGenerating,
    saveConversation,
  ]);

  return {
    thinking,
    status,
    intent,
    isGenerating,
    isFixing,
    isLoadingConversation,
    error,
    promptHistory,
    configSnapshots,
    responseSummaries,
    conversationTitle,
    questions,
    conversations,
    conversationId,
    continueWithAnswers,
    skipQuestions,
    generate,
    update,
    fixCharts,
    restoreConfig,
    reset,
    saveConversation,
    loadConversation,
    fetchConversations,
  };
}
